import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const WideFormatCarousel = () => {
  const [currentAppIndex, setCurrentAppIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showcaseRef, showcaseInView] = useInView();
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const totalApps = 5;

  // Auto-change toutes les 6 secondes avec animation smooth
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      // Commence l'animation de disparition
      setIsTransitioning(true);
      
      // Après 700ms (durée de l'animation de disparition), change l'app
      setTimeout(() => {
        setCurrentAppIndex((prev) => (prev + 1) % totalApps);
        setIsTransitioning(false); // Déclenche l'animation d'apparition
      }, 700);
    }, 6000); // Change toutes les 6 secondes

    return () => clearInterval(interval);
  }, [isPaused]);

  // Données des 5 apps avec 3 interfaces chacune
  const apps = [
    {
      name: 'Application pour restaurant',
      interfaces: [
        { src: '/references/mobile-showcase-1.png', alt: 'mobile-showcase-1' },
        { src: '/references/mobile-showcase-2.png', alt: 'mobile-showcase-2' },
        { src: '/references/mobile-showcase-3.png', alt: 'mobile-showcase-3' }
      ]
    },
    {
      name: 'Application pour plateforme de voyage',
      interfaces: [
        { src: '/references/app2-interface-1.png', alt: 'app2-interface-1' },
        { src: '/references/app2-interface-2.png', alt: 'app2-interface-2' },
        { src: '/references/app2-interface-3.png', alt: 'app2-interface-3' }
      ]
    },
    {
      name: 'Application de vente de robe',
      interfaces: [
        { src: '/references/app3-interface-1.png', alt: 'app3-interface-1' },
        { src: '/references/app3-interface-2.png', alt: 'app3-interface-2' },
        { src: '/references/app3-interface-3.png', alt: 'app3-interface-3' }
      ]
    },
    {
      name: 'Application de néo-banque',
      interfaces: [
        { src: '/references/app4-interface-1.png', alt: 'app4-interface-1' },
        { src: '/references/app4-interface-2.png', alt: 'app4-interface-2' },
        { src: '/references/app4-interface-3.png', alt: 'app4-interface-3' }
      ]
    },
    {
      name: 'Application de club de sport',
      interfaces: [
        { src: '/references/app5-interface-1.png', alt: 'app5-interface-1' },
        { src: '/references/app5-interface-2.png', alt: 'app5-interface-2' },
        { src: '/references/app5-interface-3.png', alt: 'app5-interface-3' }
      ]
    }
  ];

  const currentApp = apps[currentAppIndex];

  // Fonction pour aller à l'app suivante
  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentAppIndex((prev) => (prev + 1) % totalApps);
      setIsTransitioning(false);
    }, 700);
  };

  // Fonction pour aller à l'app précédente
  const goToPrevious = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentAppIndex((prev) => (prev - 1 + totalApps) % totalApps);
      setIsTransitioning(false);
    }, 700);
  };

  // Gestion du swipe tactile (mobile uniquement)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div className="relative z-10 mt-24 w-full">
      {/* Section 3 interfaces mobiles - Change automatiquement toutes les 6s avec animation smooth */}
      <div 
        ref={showcaseRef} 
        className="mt-8 flex flex-row justify-center items-end gap-4 md:gap-[50px]"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Interface gauche - arrive en 2ème */}
        <div 
          key={`left-${currentAppIndex}`}
          className={`relative w-[128.8px] md:w-[328.32px] h-[257.6px] md:h-[640.32px] ${
            isTransitioning ? 'animate-fade-out' : (showcaseInView ? 'animate-slide-showcase-left' : 'opacity-0')
          }`}
          style={{ 
            animationDelay: !isTransitioning && showcaseInView ? '0.4s' : '0s',
            animationFillMode: 'forwards'
          }}
        >
          <img 
            src={currentApp.interfaces[0].src}
            alt={currentApp.interfaces[0].alt}
            className="w-full h-full object-contain rounded-[15px] md:rounded-[20px]"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Placeholder */}
          <div className="hidden absolute inset-0 flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg">
            <div className="w-16 h-16 bg-gradient-to-br from-[#DC1A5F] to-[#32D6E0] rounded-xl mb-3 flex items-center justify-center text-white text-2xl font-bold">
              {currentAppIndex + 1}-1
            </div>
            <p className="text-xs text-gray-600 px-4 text-center">
              {currentApp.name} - Interface 1
            </p>
          </div>
        </div>

        {/* Interface centrale - arrive en 1er */}
        <div 
          key={`center-${currentAppIndex}`}
          className={`relative w-[138px] md:w-[342px] h-[276px] md:h-[667px] ${
            isTransitioning ? 'animate-fade-out' : (showcaseInView ? 'animate-slide-showcase-bottom' : 'opacity-0')
          }`}
          style={{ 
            animationDelay: !isTransitioning && showcaseInView ? '0.2s' : '0s',
            animationFillMode: 'forwards'
          }}
        >
          <img 
            src={currentApp.interfaces[1].src}
            alt={currentApp.interfaces[1].alt}
            className="w-full h-full object-contain rounded-[15px] md:rounded-[20px]"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Placeholder */}
          <div className="hidden absolute inset-0 flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg">
            <div className="w-20 h-20 bg-gradient-to-br from-[#DC1A5F] to-[#32D6E0] rounded-xl mb-4 flex items-center justify-center text-white text-3xl font-bold">
              {currentAppIndex + 1}-2
            </div>
            <p className="text-sm text-gray-600 px-4 text-center">
              {currentApp.name} - Interface 2
            </p>
          </div>
        </div>

        {/* Interface droite - arrive en 3ème */}
        <div 
          key={`right-${currentAppIndex}`}
          className={`relative w-[128.8px] md:w-[328.32px] h-[257.6px] md:h-[640.32px] ${
            isTransitioning ? 'animate-fade-out' : (showcaseInView ? 'animate-slide-showcase-right' : 'opacity-0')
          }`}
          style={{ 
            animationDelay: !isTransitioning && showcaseInView ? '0.6s' : '0s',
            animationFillMode: 'forwards'
          }}
        >
          <img 
            src={currentApp.interfaces[2].src}
            alt={currentApp.interfaces[2].alt}
            className="w-full h-full object-contain rounded-[15px] md:rounded-[20px]"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Placeholder */}
          <div className="hidden absolute inset-0 flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg">
            <div className="w-16 h-16 bg-gradient-to-br from-[#DC1A5F] to-[#32D6E0] rounded-xl mb-3 flex items-center justify-center text-white text-2xl font-bold">
              {currentAppIndex + 1}-3
            </div>
            <p className="text-xs text-gray-600 px-4 text-center">
              {currentApp.name} - Interface 3
            </p>
          </div>
        </div>
      </div>

      {/* Indicateurs d'apps - Dots pour navigation (plus discrets) */}
      <div className="flex justify-center items-center gap-2 mt-6">
        {apps.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              if (index !== currentAppIndex) {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentAppIndex(index);
                  setIsTransitioning(false);
                }, 700);
              }
            }}
            className={`transition-all duration-300 rounded-full ${
              index === currentAppIndex
                ? 'w-6 h-1.5 bg-[#DC1A5F]'
                : 'w-1.5 h-1.5 bg-gray-400 hover:bg-gray-500'
            }`}
            aria-label={`Aller à l'app ${index + 1}`}
          />
        ))}
        
        {/* Boutons de contrôle discrets */}
        <div className="flex items-center gap-2 ml-4">
          {/* Bouton Pause/Play */}
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-300"
            aria-label={isPaused ? "Reprendre" : "Pause"}
          >
            {isPaused ? (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            )}
          </button>
          
          {/* Bouton Previous */}
          <button
            onClick={goToPrevious}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-300"
            aria-label="Précédent"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16.42 16.89l-5.77-4.07c-.56-.4-.56-1.24 0-1.63l5.77-4.11c.67-.46 1.58.01 1.58.82v8.14c0 .81-.91 1.28-1.58.82zM8 7v10c0 .55-.45 1-1 1s-1-.45-1-1V7c0-.55.45-1 1-1s1 .45 1 1z"/>
            </svg>
          </button>
          
          {/* Bouton Next */}
          <button
            onClick={goToNext}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-300"
            aria-label="Suivant"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.58 16.89l5.77-4.07c.56-.4.56-1.24 0-1.63L7.58 7.11C6.91 6.65 6 7.12 6 7.93v8.14c0 .81.91 1.28 1.58.82zM16 7v10c0 .55.45 1 1 1s1-.45 1-1V7c0-.55-.45-1-1-1s-1 .45-1 1z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ImpactCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { language } = useLanguage();

  const phrasesKeys = [
    'references.impact.phrase1',
    'references.impact.phrase2',
    'references.impact.phrase3'
  ];

  const phrases = phrasesKeys.map(key => getTranslation(language, key));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % phrases.length);
    }, 4000); // Change toutes les 4 secondes

    return () => clearInterval(interval);
  }, [phrases.length]);

  return (
    <div className="mt-16 mb-16">
      <div className="relative h-24 md:h-20 flex items-center justify-center">
        {phrases.map((phrase, index) => {
          const isActive = index === currentIndex;
          
          return (
            <div
              key={index}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ease-in-out ${
                isActive
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-95'
              }`}
            >
              <p className="text-3xl md:text-4xl lg:text-5xl font-medium text-white text-center px-6 md:px-12 leading-relaxed max-w-5xl" style={{ fontFamily: 'Caveat, cursive' }}>
                {phrase}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ReferencesSection = () => {
  const [titleRef, titleInView] = useInView();
  const [carouselRef, carouselInView] = useInView();
  const { language } = useLanguage();
  
  // États pour la gestion du drag/swipe
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const carouselScrollRef = useRef(null);
  const animationIdRef = useRef(null);
  
  // État pour la popup des interfaces
  const [showInterfacesModal, setShowInterfacesModal] = useState(false);

  // Bloquer le scroll du site quand la popup est ouverte
  useEffect(() => {
    if (showInterfacesModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showInterfacesModal]);
  
  // 13 références d'interfaces mobiles (sans 4 et 5)
  const references = [
    { id: 1, image: "/references/reference-1.png" },
    { id: 2, image: "/references/reference-2.png" },
    { id: 3, image: "/references/reference-3.png" },
    { id: 6, image: "/references/reference-6.png" },
    { id: 7, image: "/references/reference-7.png" },
    { id: 8, image: "/references/reference-8.png" },
    { id: 9, image: "/references/reference-9.png" },
    { id: 10, image: "/references/reference-10.png" },
    { id: 11, image: "/references/reference-11.png" },
    { id: 12, image: "/references/reference-12.png" },
    { id: 13, image: "/references/reference-13.png" },
    { id: 14, image: "/references/reference-14.png" },
    { id: 15, image: "/references/reference-15.png" }
  ];

  // Largeur d'une interface + gap = 338px (320px + 18px) desktop
  // Total pour 13 interfaces = 4394px
  const itemWidth = 338;
  const totalWidth = 4394; // 13 interfaces

  // Gestion du drag/swipe - Desktop
  const handleMouseDown = (e) => {
    if (!carouselScrollRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    setStartX(e.clientX);
    
    // Récupère la position actuelle de la translation
    const style = window.getComputedStyle(carouselScrollRef.current);
    const matrix = new DOMMatrix(style.transform);
    setPrevTranslate(matrix.m41);
    
    carouselScrollRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !carouselScrollRef.current) return;
    e.preventDefault();
    
    const currentX = e.clientX;
    const diff = currentX - startX;
    let newTranslate = prevTranslate + diff;
    
    // Boucle infinie : si on dépasse à droite, on revient au début
    if (newTranslate > 0) {
      newTranslate = -totalWidth + (newTranslate % totalWidth);
      setPrevTranslate(newTranslate - diff);
    }
    // Si on dépasse à gauche, on revient à la fin
    else if (newTranslate < -totalWidth) {
      newTranslate = newTranslate % totalWidth;
      setPrevTranslate(newTranslate - diff);
    }
    
    setCurrentTranslate(newTranslate);
    carouselScrollRef.current.style.transform = `translateX(${newTranslate}px)`;
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (carouselScrollRef.current) {
      carouselScrollRef.current.style.cursor = 'grab';
    }
    
    setPrevTranslate(currentTranslate);
    
    // Reprendre l'animation après 2 secondes d'inactivité
    setTimeout(() => {
      setIsPaused(false);
    }, 2000);
  };

  const handleMouseLeaveWhileDrag = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  // Pas de contrôle tactile sur mobile - uniquement défilement automatique
  // Les événements touch sont désactivés pour éviter tout contrôle manuel

  return (
    <section id="references" className="py-20 bg-black relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#DC1A5F] rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-[#32D6E0] rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Section Header avec padding */}
      <div className="container mx-auto px-4 relative z-10">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1200 ease-out ${
            titleInView 
              ? 'opacity-100 translate-y-0 scale-100 rotate-0' 
              : 'opacity-0 translate-y-20 scale-90 -rotate-1'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <span className="gradient-text">{getTranslation(language, 'references.title')}</span>
          </h2>
          <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
            {getTranslation(language, 'references.subtitle')}
          </p>
        </div>
      </div>

      {/* Wide Format Images Carousel - 1080x500 - AVANT les interfaces */}
      <WideFormatCarousel />

      {/* Bouton pour ouvrir la popup des interfaces */}
      <div className="container mx-auto px-4 relative z-10 flex justify-center mt-12 mb-24">
        <button
          onClick={() => setShowInterfacesModal(true)}
          className="px-6 py-2.5 bg-transparent border-2 border-white rounded-full font-normal text-sm transform hover:scale-105 hover:bg-white/10 transition-all duration-300 ease-in-out whitespace-nowrap"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <span className="gradient-text">Voir les interfaces</span>
        </button>
      </div>

      {/* Modal Popup pour les interfaces */}
      {showInterfacesModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4"
          onClick={() => setShowInterfacesModal(false)}
        >
          <div 
            className="bg-black/30 backdrop-blur-lg rounded-2xl shadow-2xl max-w-7xl w-full flex flex-col max-h-[98vh] md:max-h-[95vh] overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header avec bouton fermer */}
            <div className="flex-shrink-0 flex justify-center items-center p-4 md:p-6 border-b border-gray-700/50 relative">
              <h3 className="text-xl md:text-2xl font-bold text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <span className="text-[#DC1A5F]">Interfaces Mobiles</span>
              </h3>
              <button
                onClick={() => setShowInterfacesModal(false)}
                className="absolute right-4 md:right-6 w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-all duration-300 text-gray-300 hover:text-white"
                aria-label="Fermer"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenu - Carousel défilant */}
            <div className="flex-1 overflow-hidden py-4 md:py-8 flex items-center">
              <div 
                ref={carouselRef}
                className="relative w-full overflow-hidden"
              >
                <div 
                  ref={carouselScrollRef}
                  className={`flex gap-[6px] ${!isPaused && !isDragging ? 'animate-infinite-scroll' : ''}`}
                  style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                    touchAction: 'pan-y'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                >
                  {/* Double pour défilement infini - 26 interfaces au total (13x2) */}
                  {[...references, ...references].map((ref, index) => (
                <div 
                  key={`ref-${ref.id}-${index}`}
                  className="flex-shrink-0"
                  style={{ 
                    width: '320px',
                    transform: 'scale(0.75)',
                    transformOrigin: 'center center'
                  }}
                >
                  {/* Phone Mockup */}
                  <div className="relative">
                    {/* Phone Body */}
                    <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-[2.5rem] p-[3px] shadow-2xl">
                      <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-gray-600 via-gray-800 to-gray-900 opacity-50"></div>
                      
                      {/* Side Buttons */}
                      <div className="absolute left-0 top-28 w-[2px] h-12 bg-gradient-to-b from-gray-600 to-gray-800 rounded-l-sm -translate-x-[2px]"></div>
                      <div className="absolute left-0 top-44 w-[2px] h-16 bg-gradient-to-b from-gray-600 to-gray-800 rounded-l-sm -translate-x-[2px]"></div>
                      <div className="absolute left-0 top-64 w-[2px] h-16 bg-gradient-to-b from-gray-600 to-gray-800 rounded-l-sm -translate-x-[2px]"></div>
                      <div className="absolute right-0 top-36 w-[2px] h-20 bg-gradient-to-b from-gray-600 to-gray-800 rounded-r-sm translate-x-[2px]"></div>
                      
                      {/* Screen */}
                      <div className="relative bg-black rounded-[2.4rem] overflow-hidden">
                        {/* Top Bezel */}
                        <div className="bg-black h-8 flex items-center justify-between px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>
                            <div className="w-2 h-2 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full border border-gray-700"></div>
                          </div>
                          <div className="flex gap-[2px]">
                            {[...Array(20)].map((_, i) => (
                              <div key={i} className="w-[2px] h-[2px] bg-gray-800 rounded-full"></div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Screen Content */}
                        <div className="bg-white relative overflow-hidden" style={{ width: '318px', height: '630px' }}>
                          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none z-20"></div>
                          
                          <img 
                            src={ref.image} 
                            alt={`Interface ${ref.id}`}
                            draggable="false"
                            style={{ 
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: 'center center',
                              pointerEvents: 'none'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          
                          {/* Placeholder */}
                          <div className="absolute inset-0 hidden flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                            <div className="w-24 h-24 bg-gradient-to-br from-[#DC1A5F] to-[#B91651] rounded-3xl mb-6 flex items-center justify-center text-white text-4xl font-bold shadow-2xl">
                              {ref.id}
                            </div>
                            <p className="text-sm text-gray-600 px-6 text-center font-medium">
                              En attente de l'image<br/>
                              <span className="text-xs font-mono text-gray-500 bg-white px-3 py-1.5 rounded-lg mt-3 inline-block shadow-sm border border-gray-300">
                                reference-{ref.id}.png
                              </span>
                            </p>
                          </div>
                        </div>
                        
                        {/* Bottom Bezel */}
                        <div className="bg-black h-8"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Impact Statement avec padding */}
      <div className="container mx-auto px-4 relative z-10">
        <ImpactCarousel />
      </div>
    </section>
  );
};

export default ReferencesSection;
